import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  X, Plus, Trash2, Pencil, Save, ShieldCheck, UserCog, Loader2, Check, KeyRound,
  Activity, ChevronLeft, Phone, Copy, AlertTriangle, CheckCircle2,
} from "lucide-react";
import FormError from "./ui/FormError";
import { api } from "../lib/api";
import { useAuth, PERMS } from "../lib/auth";
import useEscClose from "../hooks/useEscClose";
import { confirmDialog } from "../utils/dialogs";

// Mirror FastAPI errors (string | array | http status) → readable string.
function formatApiError(e) {
  if (!e) return "Unknown error";
  const detail = e?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail.length) {
    return detail.map((d) => {
      const loc = Array.isArray(d?.loc) ? d.loc.slice(-1).join(".") : "";
      return loc ? `${loc}: ${d?.msg || "Validation error"}` : (d?.msg || "Validation error");
    }).join("; ");
  }
  if (e?.response?.status) return `HTTP ${e.response.status} ${e.response.statusText || ""}`.trim();
  return e?.message || "Network error";
}

// Strip everything but digits and keep last 10 (matches backend normalize_mobile).
function normalizeMobileInput(raw) {
  const digits = String(raw || "").replace(/\D/g, "");
  return digits.length > 10 ? digits.slice(-10) : digits;
}

const EMPTY_FORM = {
  mobile: "",
  name: "",
  role: "user",
  permissions: ["view", "print"],
};

function PermCheckboxes({ value, onChange, disabled }) {
  return (
    <div className="flex flex-wrap gap-2">
      {PERMS.map((p) => {
        const on = value.includes(p);
        return (
          <label
            key={p}
            className={`px-2.5 py-1 rounded-md border text-[11px] cursor-pointer select-none font-mono-data uppercase tracking-wider ${
              on
                ? "bg-cyan-500/15 border-cyan-500/50 text-cyan-200"
                : "bg-[#0E1A2E] border-[#1E304B] text-slate-400 hover:text-slate-200"
            } ${disabled ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            <input
              type="checkbox"
              className="hidden"
              checked={on}
              disabled={disabled}
              onChange={(e) =>
                onChange(e.target.checked
                  ? [...new Set([...value, p])]
                  : value.filter((x) => x !== p))
              }
              data-testid={`mu-perm-${p}`}
            />
            {p}
          </label>
        );
      })}
    </div>
  );
}

function fmtDate(s) {
  if (!s) return "—";
  try {
    const d = new Date(s);
    return d.toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch { return s; }
}

export default function MobileUserManagementModal({ open, onClose }) {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");      // transient success message
  const [activityFor, setActivityFor] = useState(null);  // user object when activity panel open
  const [activity, setActivity] = useState({ rows: [], loading: false });
  // Confirmation modal state for the delete flow. Kept in-app instead
  // of `window.confirm` because some school / corporate networks silently
  // block browser confirm popups and the click looked like a no-op —
  // this is EXACTLY the "I can't delete users" bug that shows up in
  // the field.
  const [confirmDelete, setConfirmDelete] = useState(null);   // user object

  useEscClose(open, () => { if (activityFor) setActivityFor(null); else onClose?.(); });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/mobile-users");
      setUsers(r.data || []);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    load();
    setForm(EMPTY_FORM);
    setEditingId(null);
    setError("");
    setToast("");
    setActivityFor(null);
  }, [open, load]);

  // Auto-dismiss the toast after 3s.
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 3000);
    return () => clearTimeout(t);
  }, [toast]);

  const sorted = useMemo(
    () => [...users].sort((a, b) => (a.created_at || "").localeCompare(b.created_at || "")),
    [users],
  );

  const startEdit = (u) => {
    setEditingId(u.id);
    setForm({
      mobile: u.mobile || "",
      name: u.name || "",
      role: u.role,
      permissions: u.role === "admin" ? PERMS : (u.permissions || []),
    });
    setError("");
    // Scroll back to top so the form is visible.
    if (typeof document !== "undefined") {
      document.querySelector('[data-testid="mu-form"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (editingId) {
        const payload = {
          name: form.name.trim(),
          role: form.role,
          permissions: form.role === "admin" ? PERMS : form.permissions,
        };
        await api.patch(`/admin/mobile-users/${editingId}`, payload);
        setToast("Coach updated");
      } else {
        const mobile = normalizeMobileInput(form.mobile);
        if (mobile.length !== 10) {
          setError("Mobile must be a 10-digit number");
          setBusy(false);
          return;
        }
        await api.post("/admin/mobile-users", {
          mobile,
          name: form.name.trim(),
          role: form.role,
          permissions: form.role === "admin" ? PERMS : form.permissions,
        });
        setToast(`Added ${mobile}. They'll set their PIN on first login.`);
      }
      await load();
      setForm(EMPTY_FORM);
      setEditingId(null);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const patchUser = async (u, body, successMsg) => {
    setBusy(true);
    setError("");
    try {
      await api.patch(`/admin/mobile-users/${u.id}`, body);
      await load();
      if (successMsg) setToast(successMsg);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = (u) => patchUser(u, { active: !u.active },
    u.active ? `Disabled ${u.mobile}` : `Enabled ${u.mobile}`);

  const toggleApproved = (u) => patchUser(u, { approved: !u.approved },
    u.approved ? `Revoked approval for ${u.mobile}` : `Approved ${u.mobile}`);

  const resetPin = async (u) => {
    if (!(await confirmDialog(
      `Reset PIN for ${u.name || u.mobile}? They will choose a new PIN on their next login.`,
      { confirmLabel: "Reset PIN", danger: true },
    ))) return;
    patchUser(u, { reset_pin: true }, `PIN cleared. ${u.mobile} will pick a new one next time they sign in.`);
  };

  const removeUser = (u) => {
    // Open our in-app confirmation. The actual DELETE fires from
    // `confirmDeleteNow` below once the coach clicks "Yes, delete".
    setError("");
    setConfirmDelete(u);
  };

  const confirmDeleteNow = async () => {
    const u = confirmDelete;
    if (!u) return;
    setBusy(true);
    setError("");
    try {
      await api.delete(`/admin/mobile-users/${u.id}`);
      setConfirmDelete(null);
      await load();
      setToast(`Deleted ${u.mobile}`);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const showActivity = async (u) => {
    setActivityFor(u);
    setActivity({ rows: [], loading: true });
    try {
      const r = await api.get(`/admin/mobile-users/${u.id}/activity?limit=100`);
      setActivity({ rows: r.data?.activity || [], loading: false });
    } catch (e) {
      setError(formatApiError(e));
      setActivity({ rows: [], loading: false });
    }
  };

  const copyMobile = async (m) => {
    try {
      await navigator.clipboard.writeText(m);
      setToast(`Copied ${m}`);
    } catch {
      setToast(`Mobile: ${m}`);
    }
  };

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[200] bg-[#040D1A]/85 backdrop-blur-sm flex items-start justify-center overflow-y-auto py-10 px-4"
      data-testid="mobile-user-mgmt-modal"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose?.(); }}
    >
      <div className="card w-full max-w-4xl p-6 md:p-7">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            {activityFor ? (
              <button
                onClick={() => setActivityFor(null)}
                className="pill !p-1.5"
                data-testid="mu-back-to-list"
                title="Back to user list"
              >
                <ChevronLeft size={14} />
              </button>
            ) : (
              <UserCog size={18} className="text-cyan-300" />
            )}
            <div className="font-display text-lg">
              {activityFor ? `Activity · ${activityFor.name || activityFor.mobile}` : "Coaches & users"}
            </div>
          </div>
          <button onClick={onClose} className="pill !p-1.5" data-testid="mu-close-btn">
            <X size={14} />
          </button>
        </div>

        {toast && (
          <div
            className="mb-3 px-3 py-2 rounded border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 text-[12px] flex items-center gap-2"
            data-testid="mu-toast"
          >
            <CheckCircle2 size={13} /> {toast}
          </div>
        )}
        {error && (
          <div className="mb-4">
            <FormError message={error} onDismiss={() => setError("")} testId="mu-error" />
          </div>
        )}

        {activityFor ? (
          <ActivityPanel rows={activity.rows} loading={activity.loading} />
        ) : (
          <>
            {/* Add / edit form */}
            <form
              onSubmit={submit}
              className="card !bg-[#0B1627] p-4 mb-6 space-y-3"
              data-testid="mu-form"
            >
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-slate-400">
                {editingId ? <Pencil size={12} /> : <Plus size={12} />}
                {editingId ? `Editing: ${form.mobile}` : "Add a coach (mobile + PIN flow)"}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] uppercase tracking-widest text-slate-400">
                    Mobile number {!editingId && <span className="text-rose-300">*</span>}
                  </label>
                  <div className="relative mt-1">
                    <Phone size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
                    <input
                      type="tel"
                      inputMode="numeric"
                      autoComplete="off"
                      required={!editingId}
                      disabled={!!editingId}
                      maxLength={14}
                      value={form.mobile}
                      onChange={(e) => setForm({ ...form, mobile: normalizeMobileInput(e.target.value) })}
                      placeholder="10-digit, e.g. 9876543210"
                      className="w-full pl-8 pr-3 py-2 bg-[#0E1A2E] border border-[#1E304B] rounded-lg text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-[#00E5FF]/60 font-mono-data disabled:opacity-60"
                      data-testid="mu-form-mobile"
                    />
                  </div>
                  {!editingId && (
                    <div className="text-[10px] text-slate-500 mt-1 leading-snug">
                      New coaches pick their own 6-10 digit PIN on first login. No PIN to share.
                    </div>
                  )}
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-widest text-slate-400">Name</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Coach's name"
                    className="w-full mt-1 px-3 py-2 bg-[#0E1A2E] border border-[#1E304B] rounded-lg text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:border-[#00E5FF]/60"
                    data-testid="mu-form-name"
                  />
                </div>
                <div>
                  <label className="text-[11px] uppercase tracking-widest text-slate-400">Role</label>
                  <select
                    value={form.role}
                    onChange={(e) => setForm({ ...form, role: e.target.value })}
                    className="w-full mt-1 px-3 py-2 bg-[#0E1A2E] border border-[#1E304B] rounded-lg text-sm text-slate-100 focus:outline-none focus:border-[#00E5FF]/60"
                    data-testid="mu-form-role"
                  >
                    <option value="user">User (granular perms)</option>
                    <option value="admin">Admin (full access)</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="text-[11px] uppercase tracking-widest text-slate-400 block mb-2">
                  Permissions {form.role === "admin" && <span className="text-cyan-300 normal-case ml-1">— admin has all</span>}
                </label>
                <PermCheckboxes
                  value={form.role === "admin" ? PERMS : form.permissions}
                  onChange={(perms) => setForm({ ...form, permissions: perms })}
                  disabled={form.role === "admin"}
                />
              </div>

              <div className="flex justify-end gap-2 pt-1">
                {editingId && (
                  <button type="button" className="pill" onClick={cancelEdit} data-testid="mu-form-cancel">
                    Cancel
                  </button>
                )}
                <button
                  type="submit"
                  className="pill active"
                  disabled={busy}
                  data-testid="mu-form-submit"
                >
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                  {editingId ? "Save changes" : "Add coach"}
                </button>
              </div>
              {error && (
                <div className="mt-2">
                  <FormError message={error} onDismiss={() => setError("")} testId="mu-error-near-save" />
                </div>
              )}
            </form>

            {/* List */}
            <div className="text-[11px] uppercase tracking-widest text-slate-400 mb-2 flex items-center justify-between">
              <span>Coaches ({sorted.length})</span>
              <span className="text-slate-500 normal-case tracking-normal text-[10px]">
                tap a row for actions · activity log shows last 100 edits/deletes
              </span>
            </div>

            <div className="card !bg-[#0B1627] p-0 overflow-hidden">
              {loading ? (
                <div className="p-6 text-center text-slate-500">
                  <Loader2 size={14} className="animate-spin inline mr-2" /> Loading coaches…
                </div>
              ) : sorted.length === 0 ? (
                <div className="p-6 text-center text-slate-500">No coaches yet. Add the first one above.</div>
              ) : (
                <div className="divide-y divide-[#1E304B]">
                  {sorted.map((u) => (
                    <UserRow
                      key={u.id}
                      u={u}
                      isSelf={u.id === me?.id}
                      onEdit={() => startEdit(u)}
                      onToggleActive={() => toggleActive(u)}
                      onToggleApproved={() => toggleApproved(u)}
                      onResetPin={() => resetPin(u)}
                      onDelete={() => removeUser(u)}
                      onActivity={() => showActivity(u)}
                      onCopy={() => copyMobile(u.mobile)}
                      busy={busy}
                    />
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Confirm-delete dialog — an in-app modal replaces `window.confirm`
          which some enterprise browsers / school WiFi silently block. */}
      {confirmDelete && (
        <div
          className="fixed inset-0 z-[220] flex items-center justify-center px-4 bg-[#040D1A]/85 backdrop-blur-sm"
          data-testid="mu-confirm-delete-modal"
          onClick={(e) => { if (e.target === e.currentTarget && !busy) setConfirmDelete(null); }}
        >
          <div className="card max-w-md w-full p-5">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: "rgba(239,68,68,0.15)" }}>
                <Trash2 size={16} className="text-rose-300" />
              </div>
              <div className="min-w-0">
                <div className="font-display text-base text-slate-100">Delete this coach?</div>
                <div className="text-[12px] text-slate-400 mt-0.5">
                  This permanently removes <span className="text-slate-200 font-mono-data">{confirmDelete.mobile}</span>
                  {confirmDelete.name && <> — <span className="text-slate-200">{confirmDelete.name}</span></>}.
                  Their audit-log entries stay intact, but they'll lose access immediately.
                </div>
              </div>
            </div>
            {error && (
              <div className="mb-3 px-3 py-2 rounded-lg bg-rose-500/10 border border-rose-500/30 text-rose-200 text-sm" data-testid="mu-confirm-delete-error">
                {error}
              </div>
            )}
            <div className="flex items-center justify-end gap-2">
              <button
                className="pill"
                onClick={() => setConfirmDelete(null)}
                disabled={busy}
                data-testid="mu-confirm-delete-cancel"
              >
                Cancel
              </button>
              <button
                className="pill"
                onClick={confirmDeleteNow}
                disabled={busy}
                style={{
                  background: "rgba(239,68,68,0.15)",
                  borderColor: "rgba(239,68,68,0.5)",
                  color: "#FCA5A5",
                }}
                data-testid="mu-confirm-delete-confirm"
              >
                <Trash2 size={12} /> {busy ? "Deleting…" : "Yes, delete coach"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
}

function UserRow({ u, isSelf, onEdit, onToggleActive, onToggleApproved, onResetPin,
                  onDelete, onActivity, onCopy, busy }) {
  return (
    <div
      className="px-4 py-3 flex flex-wrap items-center gap-3"
      data-testid={`mu-row-${u.id}`}
    >
      <div className="flex-1 min-w-[200px]">
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={onCopy}
            className="font-mono-data text-sm text-slate-100 hover:text-cyan-200 inline-flex items-center gap-1"
            title="Copy mobile to clipboard"
            data-testid={`mu-copy-${u.id}`}
          >
            <Phone size={11} className="text-slate-500" />
            {u.mobile || "—"}
            <Copy size={10} className="opacity-50" />
          </button>
          {u.role === "admin" && (
            <span className="px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider bg-amber-500/15 border border-amber-500/40 text-amber-200 flex items-center gap-0.5">
              <ShieldCheck size={10} /> Admin
            </span>
          )}
          {!u.active && (
            <span className="px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider bg-slate-700/40 border border-slate-600/50 text-slate-300">
              Disabled
            </span>
          )}
          {u.approved === false && (
            <span className="px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider bg-rose-500/15 border border-rose-500/40 text-rose-200 flex items-center gap-0.5">
              <AlertTriangle size={9} /> Pending approval
            </span>
          )}
          {!u.pin_set && (
            <span className="px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider bg-cyan-500/10 border border-cyan-500/30 text-cyan-200 font-mono-data">
              PIN not set
            </span>
          )}
          {isSelf && (
            <span className="px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider bg-emerald-500/10 border border-emerald-500/30 text-emerald-200">
              You
            </span>
          )}
        </div>
        {u.name && <div className="text-[12px] text-slate-300 mt-0.5">{u.name}</div>}
        <div className="text-[10px] text-slate-500 font-mono-data mt-0.5">
          last login {fmtDate(u.last_login_at)} · {u.login_count || 0} sign-ins
        </div>
      </div>

      <div className="flex flex-wrap gap-1">
        {(u.role === "admin" ? PERMS : u.permissions || []).map((p) => (
          <span
            key={p}
            className="px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider bg-cyan-500/10 border border-cyan-500/30 text-cyan-200 font-mono-data flex items-center gap-0.5"
          >
            <Check size={9} /> {p}
          </span>
        ))}
        {u.role !== "admin" && (!u.permissions || u.permissions.length === 0) && (
          <span className="text-[10px] text-slate-500 italic">no perms</span>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5 ml-auto">
        <button
          className="pill !text-[11px] !py-1 !px-2.5"
          onClick={onActivity}
          disabled={busy}
          data-testid={`mu-activity-${u.id}`}
          title="View this coach's recent edits / deletes"
        >
          <Activity size={11} /> Activity
        </button>
        <button
          className="pill !text-[11px] !py-1 !px-2.5"
          onClick={onResetPin}
          disabled={busy}
          data-testid={`mu-reset-pin-${u.id}`}
          title="Force this coach to pick a new PIN on next login"
        >
          <KeyRound size={11} /> Reset PIN
        </button>
        {u.approved === false ? (
          <button
            className="pill !text-[11px] !py-1 !px-2.5"
            style={{ color: "#86EFAC", borderColor: "rgba(16,185,129,0.4)" }}
            onClick={onToggleApproved}
            disabled={busy}
            data-testid={`mu-approve-${u.id}`}
          >
            Approve
          </button>
        ) : (
          !isSelf && (
            <button
              className="pill !text-[11px] !py-1 !px-2.5"
              onClick={onToggleActive}
              disabled={busy}
              data-testid={`mu-toggle-active-${u.id}`}
            >
              {u.active ? "Disable" : "Enable"}
            </button>
          )
        )}
        <button
          className="pill !text-[11px] !py-1 !px-2.5"
          onClick={onEdit}
          disabled={busy}
          data-testid={`mu-edit-${u.id}`}
        >
          <Pencil size={11} /> Edit
        </button>
        {!isSelf && (
          <button
            className="pill !text-[11px] !py-1 !px-2.5"
            style={{ color: "#FCA5A5", borderColor: "rgba(239,68,68,0.4)" }}
            onClick={onDelete}
            disabled={busy}
            data-testid={`mu-delete-${u.id}`}
          >
            <Trash2 size={11} /> Delete
          </button>
        )}
      </div>
    </div>
  );
}

function ActivityPanel({ rows, loading }) {
  if (loading) {
    return (
      <div className="card !bg-[#0B1627] p-6 text-center text-slate-500">
        <Loader2 size={14} className="animate-spin inline mr-2" /> Loading activity…
      </div>
    );
  }
  if (!rows.length) {
    return (
      <div className="card !bg-[#0B1627] p-6 text-center text-slate-500 text-[12px]">
        No audit-log entries yet for this coach. Edits and deletions they make will show up here.
      </div>
    );
  }
  return (
    <div className="card !bg-[#0B1627] p-0 overflow-hidden" data-testid="mu-activity-panel">
      <div className="divide-y divide-[#1E304B] max-h-[60vh] overflow-y-auto">
        {rows.map((r) => (
          <div key={r.id} className="px-4 py-2.5 text-[12px]" data-testid={`mu-activity-row-${r.id}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono-data text-[10px] text-slate-500">{fmtDate(r.ts)}</span>
              <span className={`px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider font-mono-data ${
                r.action === "delete"
                  ? "bg-rose-500/10 border border-rose-500/30 text-rose-200"
                  : r.action === "create"
                  ? "bg-emerald-500/10 border border-emerald-500/30 text-emerald-200"
                  : "bg-cyan-500/10 border border-cyan-500/30 text-cyan-200"
              }`}>
                {r.action}
              </span>
              <span className="text-slate-400 font-mono-data text-[10px]">{r.entity}</span>
              <span className="text-slate-100">{r.entity_label || r.entity_id}</span>
              {r.restored_at && (
                <span className="px-1.5 py-0.5 rounded text-[9px] bg-amber-500/10 border border-amber-500/30 text-amber-200 font-mono-data">
                  restored
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
